import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";

const DEFAULTS = { brand_title: "Exxpressions Interiors", brand_logo_url: "/logo.png" };

const BrandingContext = createContext(null);

/** Backend-hosted logos come back as an /api/... path; anything else (a bundled
 *  asset like /logo.png, or a full URL) is used as-is. */
export function resolveLogoSrc(url) {
  if (!url) return "";
  return url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url;
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS);

  const refreshBranding = useCallback(async () => {
    try {
      const { data } = await api.get("/branding");
      setBranding({ ...DEFAULTS, ...data });
      return data;
    } catch {
      // Branding is cosmetic — keep the defaults rather than blocking the app.
      return null;
    }
  }, []);

  useEffect(() => { refreshBranding(); }, [refreshBranding]);

  const brandTitle = branding.brand_title || DEFAULTS.brand_title;

  useEffect(() => {
    document.title = `${brandTitle} — Daily Work Management`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", `${brandTitle} — Daily Work Management`);
  }, [brandTitle]);

  return (
    <BrandingContext.Provider
      value={{ brandTitle, logoUrl: branding.brand_logo_url || "", refreshBranding }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

// Stable object so a component rendered outside the provider (tests, storybook)
// never re-runs effects that depend on refreshBranding.
const FALLBACK = {
  brandTitle: DEFAULTS.brand_title,
  logoUrl: DEFAULTS.brand_logo_url,
  refreshBranding: async () => null,
};

export const useBranding = () => useContext(BrandingContext) || FALLBACK;
